/**
 * Custom hook for clause state management
 * Integrates with RTK Query and provides convenient methods
 */

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  useGetActionableClausesQuery,
  useGetClausePropertiesQuery,
  useGetClausesRequiringAttentionQuery,
  useGetNegotiationSummaryQuery,
  useGetUrgentClausesQuery,
  useHasPendingNegotiationsQuery,
  useInitializeContractClausesMutation,
  useUpdateClauseStatusMutation,
} from '@/apis/extended/clausePropertiesApi';
import { ClauseProperties } from '@/utils/clauseIdUtils';

export interface UseClauseStateOptions {
  contractId: string;
  userRole: 'buyer' | 'seller' | 'agent';
  contractRatified?: boolean;
}

export const useClauseState = ({
  contractId,
  userRole,
  contractRatified = false,
}: UseClauseStateOptions) => {
  const dispatch = useDispatch();

  // Debug logging
  console.log('useClauseState - contractId:', contractId);
  console.log('useClauseState - userRole:', userRole);
  console.log('useClauseState - contractRatified:', contractRatified);

  // Early return if no contractId
  if (!contractId) {
    console.log('useClauseState - No contractId, returning early fallback');
    return {
      clauses: [],
      actionableClauses: [],
      attentionClauses: [],
      urgentClauses: [],
      negotiationSummary: {
        totalClauses: 0,
        acceptedClauses: 0,
        pendingClauses: 0,
        rejectedClauses: 0,
        ratifiedClauses: 0,
      },
      hasPendingNegotiations: false,
      isLoadingClauses: false,
      isErrorClauses: false,
      isLoadingActionable: false,
      isLoadingAttention: false,
      isLoadingUrgent: false,
      isLoadingSummary: false,
      isLoadingPending: false,
      isUpdatingStatus: false,
      isInitializing: false,
      isBackendUnavailable: true,
      handleUpdateClauseStatus: () => {},
      handleInitializeClauses: () => {},
      handleCounterClause: () => {},
      handleAcceptClause: () => {},
      handleRejectClause: () => {},
      handleRatifyClause: () => {},
      refetchClauses: () => {},
      getClausesByStatus: () => [],
      getClausesByAction: () => [],
      canActOnClause: () => false,
      getClausesRequiringUserAttention: () => [],
      getUrgentClausesForUser: () => [],
    };
  }

  // Queries - only run if contractId is provided
  const {
    data: clausePropertiesData,
    isLoading: isLoadingClauses,
    isError: isErrorClauses,
    refetch: refetchClauses,
  } = useGetClausePropertiesQuery(
    {
      contractId,
      userRole,
      contractRatified,
    },
    {
      skip: !contractId,
    }
  );

  const { data: actionableClausesData, isLoading: isLoadingActionable } =
    useGetActionableClausesQuery(
      {
        contractId,
        userRole,
        action: 'counter', // Default action, can be overridden
      },
      {
        skip: !contractId,
      }
    );

  const { data: attentionClausesData, isLoading: isLoadingAttention } =
    useGetClausesRequiringAttentionQuery(
      {
        contractId,
        userRole,
      },
      {
        skip: !contractId,
      }
    );

  const { data: urgentClausesData, isLoading: isLoadingUrgent } =
    useGetUrgentClausesQuery(
      {
        contractId,
        userRole,
      },
      {
        skip: !contractId,
      }
    );

  const { data: negotiationSummaryData, isLoading: isLoadingSummary } =
    useGetNegotiationSummaryQuery(
      {
        contractId,
      },
      {
        skip: !contractId,
      }
    );

  const { data: pendingNegotiationsData, isLoading: isLoadingPending } =
    useHasPendingNegotiationsQuery(
      {
        contractId,
      },
      {
        skip: !contractId,
      }
    );

  // Mutations
  const [updateClauseStatus, { isLoading: isUpdatingStatus }] =
    useUpdateClauseStatusMutation();
  const [initializeContractClauses, { isLoading: isInitializing }] =
    useInitializeContractClausesMutation();

  // Computed values
  const clauses = clausePropertiesData?.data || [];
  const actionableClauses = actionableClausesData?.data || [];
  const attentionClauses = attentionClausesData?.data || [];
  const urgentClauses = urgentClausesData?.data || [];
  const negotiationSummary = negotiationSummaryData?.data;
  const hasPendingNegotiations =
    pendingNegotiationsData?.data?.hasPending || false;

  // Actions
  const handleUpdateClauseStatus = useCallback(
    async (clauseId: string, newStatus: string, reason?: string) => {
      try {
        const result = await updateClauseStatus({
          contractId,
          clauseId,
          newStatus,
          modifiedBy: userRole,
          reason,
        }).unwrap();

        return result;
      } catch (error) {
        console.error('Error updating clause status:', error);
        throw error;
      }
    },
    [updateClauseStatus, contractId, userRole]
  );

  const handleInitializeClauses = useCallback(
    async (clauseIds: string[]) => {
      try {
        const result = await initializeContractClauses({
          contractId,
          clauseIds,
        }).unwrap();

        return result;
      } catch (error) {
        console.error('Error initializing clauses:', error);
        throw error;
      }
    },
    [initializeContractClauses, contractId]
  );

  const handleCounterClause = useCallback(
    async (clauseId: string, reason?: string) => {
      return handleUpdateClauseStatus(clauseId, 'proposed', reason);
    },
    [handleUpdateClauseStatus]
  );

  const handleAcceptClause = useCallback(
    async (clauseId: string, reason?: string) => {
      return handleUpdateClauseStatus(clauseId, 'accepted', reason);
    },
    [handleUpdateClauseStatus]
  );

  const handleRejectClause = useCallback(
    async (clauseId: string, reason?: string) => {
      return handleUpdateClauseStatus(clauseId, 'rejected', reason);
    },
    [handleUpdateClauseStatus]
  );

  const handleRatifyClause = useCallback(
    async (clauseId: string, reason?: string) => {
      return handleUpdateClauseStatus(clauseId, 'ratified', reason);
    },
    [handleUpdateClauseStatus]
  );

  // Filter clauses by status
  const getClausesByStatus = useCallback(
    (status: string) => {
      return clauses.filter((clause) => clause.status === status);
    },
    [clauses]
  );

  // Filter clauses by actionability
  const getClausesByAction = useCallback(
    (action: 'edit' | 'counter' | 'accept' | 'reject') => {
      return clauses.filter((clause) => {
        const actionMap = {
          edit: clause.canEdit,
          counter: clause.canCounter,
          accept: clause.canAccept,
          reject: clause.canReject,
        };
        return actionMap[action].includes(userRole);
      });
    },
    [clauses, userRole]
  );

  // Check if clause can be acted upon
  const canActOnClause = useCallback(
    (
      clause: ClauseProperties,
      action: 'edit' | 'counter' | 'accept' | 'reject'
    ) => {
      const actionMap = {
        edit: clause.canEdit,
        counter: clause.canCounter,
        accept: clause.canAccept,
        reject: clause.canReject,
      };
      return actionMap[action].includes(userRole);
    },
    [userRole]
  );

  // Get clauses that require user attention
  const getClausesRequiringUserAttention = useCallback(() => {
    return clauses.filter(
      (clause) =>
        clause.requiresAttention &&
        clause.visibleTo.includes(userRole) &&
        !clause.hiddenFrom.includes(userRole)
    );
  }, [clauses, userRole]);

  // Get urgent clauses for user
  const getUrgentClausesForUser = useCallback(() => {
    return clauses.filter(
      (clause) =>
        clause.isUrgent &&
        clause.visibleTo.includes(userRole) &&
        !clause.hiddenFrom.includes(userRole)
    );
  }, [clauses, userRole]);

  // Check if backend is unavailable (404 errors)
  const isBackendUnavailable =
    isErrorClauses &&
    (clausePropertiesData as any)?.error &&
    typeof (clausePropertiesData as any).error === 'object' &&
    'status' in (clausePropertiesData as any).error &&
    (clausePropertiesData as any).error.status === 404;

  // Debug logging for error handling
  console.log('useClauseState - isErrorClauses:', isErrorClauses);
  console.log('useClauseState - clausePropertiesData:', clausePropertiesData);
  console.log('useClauseState - isBackendUnavailable:', isBackendUnavailable);

  // Provide fallback data when backend is unavailable
  const fallbackClauses = isBackendUnavailable ? [] : clauses;
  const fallbackActionableClauses = isBackendUnavailable
    ? []
    : actionableClauses;
  const fallbackAttentionClauses = isBackendUnavailable ? [] : attentionClauses;
  const fallbackUrgentClauses = isBackendUnavailable ? [] : urgentClauses;
  const fallbackNegotiationSummary = isBackendUnavailable
    ? {
        totalClauses: 0,
        acceptedClauses: 0,
        pendingClauses: 0,
        rejectedClauses: 0,
        ratifiedClauses: 0,
      }
    : negotiationSummary;
  const fallbackHasPendingNegotiations = isBackendUnavailable
    ? false
    : hasPendingNegotiations;

  return {
    // Data
    clauses: fallbackClauses,
    actionableClauses: fallbackActionableClauses,
    attentionClauses: fallbackAttentionClauses,
    urgentClauses: fallbackUrgentClauses,
    negotiationSummary: fallbackNegotiationSummary,
    hasPendingNegotiations: fallbackHasPendingNegotiations,

    // Loading states
    isLoadingClauses,
    isErrorClauses,
    isLoadingActionable,
    isLoadingAttention,
    isLoadingUrgent,
    isLoadingSummary,
    isLoadingPending,
    isUpdatingStatus,
    isInitializing,
    isBackendUnavailable,

    // Actions
    handleUpdateClauseStatus,
    handleInitializeClauses,
    handleCounterClause,
    handleAcceptClause,
    handleRejectClause,
    handleRatifyClause,
    refetchClauses,

    // Utilities
    getClausesByStatus,
    getClausesByAction,
    canActOnClause,
    getClausesRequiringUserAttention,
    getUrgentClausesForUser,
  };
};
